import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import { IconButton, Select, MenuItem, Tabs, Tab, Box } from '@mui/material';
import { Checkbox, FormControlLabel } from '@mui/material';
import styled from 'styled-components';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { DistrictSelectInput } from '../DistrictSelectInput';
import { SelectDistrictType } from '../../components';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import type { CoverageArea, CoveragePlan, DistrictId, MeasuresCoveragePlan } from '../../types/coverage-area';
import type { District } from '../../app/feathers/districts/District';

const CHUNK_SIZE = 20;

/** Minimal district shape for display (id + longName). */
type DistrictDisplay = Pick<District, '_id' | 'longName'>;

export interface EditCoveragePlanProps {
  value?: CoverageArea | null;
  stateId?: string | null;
  onChange: (next: CoverageArea) => void;
  stateName?: string | null;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Controlled form for editing a single CoverageArea.
 * No local state for form fields; all read from value and written via onChange.
 * Enriched district metadata is loaded in useEffect (by id) and kept in local state for display.
 */
const EditCoveragePlan = ({
  value,
  stateId,
  onChange,
  stateName,
  style,
  className
}: EditCoveragePlanProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [districtsById, setDistrictsById] = useState<Record<string, DistrictDisplay>>({});
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [humanizedDistrictTypes, setHumanizedDistrictTypes] = useState<Record<string, string>>({});
  const districtsByIdRef = useRef<Record<string, DistrictDisplay>>({});
  districtsByIdRef.current = districtsById;

  const v = value ?? {};
  const coveragePlan: CoveragePlan = (v.coveragePlan ?? 'auto-coverage') as CoveragePlan;
  const coveragePlanMeasures = v.coveragePlanMeasures as MeasuresCoveragePlan | undefined;
  const useMeasuresCoveragePlanOverride = typeof coveragePlanMeasures !== 'undefined';
  const supportedAreas: DistrictId[] = v.supportedAreas ?? [];
  const supportedDistrictTypes: string[] = v.supportedDistrictTypes ?? [];
  const priorityArea: DistrictId[] = v.priorityArea ?? [];

  const stateIdStr = stateId ? `${stateId}` : '';
  const coverageType = useMemo(() => {
    if (supportedAreas.length === 1 && `${supportedAreas[0]}` === stateIdStr) return 'statewide';
    return 'districts';
  }, [supportedAreas, stateIdStr]);

  /** Tab index: 0 = Limited coverage (districts), 1 = Statewide */
  const coverageTabIndex = coverageType === 'districts' ? 0 : 1;

  const allDistrictIds = useMemo(() => {
    const ids = [...supportedAreas, ...priorityArea];
    if (coverageType === 'statewide' && stateIdStr) ids.push(stateIdStr);
    return ids;
  }, [supportedAreas, priorityArea, coverageType, stateIdStr]);

  useEffect(() => {
    if (!feathers) return;
    if (!allDistrictIds.length) {
      setDistrictsById({});
      return;
    }
    const missingIds = allDistrictIds.filter(id => !districtsByIdRef.current[`${id}`]);
    if (!missingIds.length) return;

    let cancelled = false;
    setLoadingDistricts(true);
    loadDistrictsById(feathers, missingIds)
      .then(byId => {
        if (!cancelled) setDistrictsById(prev => ({ ...prev, ...byId }));
      })
      .finally(() => {
        if (!cancelled) setLoadingDistricts(false);
      });
    return () => { cancelled = true; };
  }, [feathers, allDistrictIds.join(',')]);

  const handleChange = (next: Partial<CoverageArea>) => {
    onChange({ ...v, ...next });
  };

  const setCoveragePlan = (coveragePlan: CoveragePlan) => {
    handleChange({ coveragePlan });
  };

  const setUseMeasuresCoveragePlanOverride = (enabled: boolean) => {
    if (!enabled) {
      handleChange({ coveragePlanMeasures: undefined });
      return;
    }
    const defaultMeasuresPlan: MeasuresCoveragePlan = coveragePlan === 'no-coverage'
      ? 'no-coverage'
      : 'auto-coverage';
    handleChange({ coveragePlanMeasures: defaultMeasuresPlan });
  };

  const setCoveragePlanMeasures = (nextMeasuresPlan: MeasuresCoveragePlan) => {
    handleChange({ coveragePlanMeasures: nextMeasuresPlan });
  };

  const setCoverageType = (type: 'statewide' | 'districts') => {
    if (type === 'statewide') {
      handleChange({ supportedAreas: [stateId!], supportedDistrictTypes: [] });
    } else {
      handleChange({ supportedAreas: [] });
    }
  };

  const addToSupportedArea = (district: DistrictDisplay | District | string | null) => {
    const id = (district as { _id?: string })?._id ?? (typeof district === 'string' ? district : null);
    if (!id || supportedAreas.some(s => `${s}` === `${id}`)) return;
    handleChange({ supportedAreas: [...supportedAreas, id] });
  };

  const removeFromSupportedArea = (districtId: DistrictId) => {
    handleChange({
      supportedAreas: supportedAreas.filter(s => `${s}` !== `${districtId}`),
      priorityArea: priorityArea.filter(p => `${p}` !== `${districtId}`)
    });
  };

  const togglePriorityArea = (districtId: DistrictId) => {
    const isPriority = priorityArea.some(p => `${p}` === `${districtId}`);
    handleChange({
      priorityArea: isPriority
        ? priorityArea.filter(p => `${p}` !== `${districtId}`)
        : [...priorityArea, districtId]
    });
  };

  const addDistrictType = (districtType: string, humanizedType?: string) => {
    if (supportedDistrictTypes.includes(districtType)) return;
    setHumanizedDistrictTypes(prev => ({ ...prev, [districtType]: humanizedType ?? humanizeDistrictType(districtType, null) }));
    handleChange({ supportedDistrictTypes: [...supportedDistrictTypes, districtType] });
  };

  const removeDistrictType = (districtType: string) => {
    setHumanizedDistrictTypes(prev => {
      const next = { ...prev };
      delete next[districtType];
      return next;
    });
    handleChange({ supportedDistrictTypes: supportedDistrictTypes.filter(dt => dt !== districtType) });
  };

  const removeFromPriorityArea = (districtId: DistrictId) => {
    handleChange({ priorityArea: priorityArea.filter(p => `${p}` !== `${districtId}`) });
  };

  if (!value && !stateId) {
    return (
      <Wrapper style={style} className={className}>
        <Typography variant='body1'>Loading...</Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper style={style} className={className}>
      <Section>
        <Typography variant='body2'>What type of research do we need for this area?</Typography>
        <Select
          value={coveragePlan}
          onChange={e => setCoveragePlan(e.target.value as CoveragePlan)}
          variant='outlined'
          style={{ minWidth: '200px' }}
        >
          <MenuItem value='auto-coverage'>
            <div>
              <div><strong>Auto Coverage</strong></div>
              <div style={{ fontSize: '0.8em', color: '#666' }}>Full cadidate profiles, complete with AI-generated content and human review.</div>
            </div>
          </MenuItem>
          <MenuItem value='basic-coverage'>
            <div>
              <div><strong>Basic Coverage</strong></div>
              <div style={{ fontSize: '0.8em', color: '#666' }}>Full candidate lists and candidate metadata, including website, social media, and photo.</div>
            </div>
          </MenuItem>
          <MenuItem value='no-coverage'>
            <div>
              <div><strong>No Coverage</strong></div>
              <div style={{ fontSize: '0.8em', color: '#666' }}>Only candidate name and party.</div>
            </div>
          </MenuItem>
        </Select>
        <FormControlLabel
          control={(
            <Checkbox
              checked={useMeasuresCoveragePlanOverride}
              onChange={(e) => setUseMeasuresCoveragePlanOverride(e.target.checked)}
            />
          )}
          label='Set different plan for measures'
        />
        {useMeasuresCoveragePlanOverride && (
          <Select
            value={coveragePlanMeasures ?? 'auto-coverage'}
            onChange={e => setCoveragePlanMeasures(e.target.value as MeasuresCoveragePlan)}
            variant='outlined'
            style={{ minWidth: '200px' }}
          >
            <MenuItem value='auto-coverage'>
              <div>
                <div><strong>Full explanations</strong></div>
                <div style={{ fontSize: '0.8em', color: '#666' }}>Measure content follows full profile coverage.</div>
              </div>
            </MenuItem>
            <MenuItem value='no-coverage'>
              <div>
                <div><strong>Measure lists</strong></div>
                <div style={{ fontSize: '0.8em', color: '#666' }}>Measures default to no coverage.</div>
              </div>
            </MenuItem>
          </Select>
        )}
      </Section>
      <Section style={{ marginTop: '24px' }}>
        <Tabs
          value={coverageTabIndex}
          onChange={(_e, newIndex: number) => setCoverageType(newIndex === 0 ? 'districts' : 'statewide')}
          variant='fullWidth'
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
        >
          <Tab label='Limited coverage' id='coverage-tab-0' aria-controls='coverage-panel-0' />
          <Tab label='Statewide' id='coverage-tab-1' aria-controls='coverage-panel-1' />
        </Tabs>
        <Box role='tabpanel' id='coverage-panel-0' aria-labelledby='coverage-tab-0' hidden={coverageTabIndex !== 0} sx={{ pt: 2 }}>
          <Section style={{ marginBottom: '36px', maxWidth: '300px' }}>
            <Typography variant='body1' style={{ fontWeight: 'bold' }}>District types</Typography>
            {supportedDistrictTypes.map((dt, i) => (
              <SupportedDistrictRow key={i}>
                <Typography variant='body1'>{humanizedDistrictTypes[dt] ?? humanizeDistrictType(dt, null)}</Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconButton size='small' onClick={() => removeDistrictType(dt)}>
                    <CloseIcon style={{ height: '14px', width: '14px' }} />
                  </IconButton>
                </div>
              </SupportedDistrictRow>
            ))}
            {stateName && (
              <Box style={{ maxWidth: '300px' }}>
                <SelectDistrictType
                  stateName={stateName}
                  value={null}
                  onChange={(type, humanizedType) => type && addDistrictType(type, humanizedType)}
                  excludeTypes={supportedDistrictTypes}
                  restrictToTiers={[1, 2]}
                  placeholder='Select district type'
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '& .MuiSelect-select': { backgroundColor: '#fff' },
                  }}
                />
              </Box>
            )}
          </Section>
          <Section style={{ marginTop: '64px', maxWidth: '300px' }}>
            <Typography variant='body1' style={{ fontWeight: 'bold' }}>Specific areas</Typography>
            {supportedAreas.map((id, i) => {
              const district = districtsById[`${id}`];
              const isPriority = priorityArea.some(p => `${p}` === `${id}`);
              return (
                <SupportedDistrictRow key={`${id}-${i}`}>
                  <Typography variant='body1'>
                    {loadingDistricts && !district ? 'Loading...' : (district?.longName ?? id)}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconButton
                      size='small'
                      onClick={() => togglePriorityArea(id)}
                      title={isPriority ? 'Remove from priority area' : 'Add to priority area'}
                    >
                      {isPriority ? (
                        <StarIcon style={{ height: '16px', width: '16px', color: '#ffa726' }} />
                      ) : (
                        <StarBorderIcon style={{ height: '16px', width: '16px', color: '#666' }} />
                      )}
                    </IconButton>
                    <IconButton size='small' onClick={() => removeFromSupportedArea(id)}>
                      <CloseIcon style={{ height: '14px', width: '14px' }} />
                    </IconButton>
                  </div>
                </SupportedDistrictRow>
              );
            })}
            <DistrictSelectInput
              onChange={(e) => addToSupportedArea(e.target.value as DistrictDisplay | District | string | null)}
              value={null}
              style={{ marginTop: '4px' }}
              districtTypes={['county', 'city', 'congressional']}
              descendantOf={[stateId!]}
              clearAfterSelect
              placeholder='Add a district'
              startAdornment={<AddIcon sx={{ color: 'action.active', fontSize: 20 }} />}
            />
          </Section>
        </Box>
        <Box role='tabpanel' id='coverage-panel-1' aria-labelledby='coverage-tab-1' hidden={coverageTabIndex !== 1} sx={{ pt: 2 }}>
          <Section style={{ maxWidth: '300px' }}>
            <Typography variant='body1' style={{ fontWeight: 'bold' }}>Priority districts</Typography>
            {priorityArea.map((id, i) => {
              const district = districtsById[`${id}`];
              return (
                <SupportedDistrictRow key={`${id}-${i}`}>
                  <Typography variant='body1'>
                    {loadingDistricts && !district ? 'Loading...' : (district?.longName ?? id)}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StarIcon style={{ height: '16px', width: '16px', color: '#ffa726' }} />
                    <IconButton size='small' onClick={() => removeFromPriorityArea(id)}>
                      <CloseIcon style={{ height: '14px', width: '14px' }} />
                    </IconButton>
                  </div>
                </SupportedDistrictRow>
              );
            })}
            <DistrictSelectInput
              onChange={(e) => {
                const newDistrict = e.target.value as DistrictDisplay | District | string | null;
                const id = (newDistrict as { _id?: string })?._id ?? (typeof newDistrict === 'string' ? newDistrict : null);
                if (id && !priorityArea.some(p => `${p}` === `${id}`)) {
                  handleChange({ priorityArea: [...priorityArea, id] });
                }
              }}
              value={null}
              style={{ marginTop: '4px' }}
              districtTypes={['county', 'city', 'congressional']}
              descendantOf={[stateId!]}
              clearAfterSelect
              placeholder='Add a district'
              startAdornment={<AddIcon sx={{ color: 'action.active', fontSize: 20 }} />}
            />
          </Section>
        </Box>
      </Section>
    </Wrapper>
  );
};

/**
 * Load district ids in chunks (handles pagination limits). Returns map id -> district.
 * All chunks are fetched in parallel with Promise.all.
 */
async function loadDistrictsById(
  feathers: Application<ServiceTypes>,
  ids: DistrictId[]
): Promise<Record<string, DistrictDisplay>> {
  const uniqueIds = [...new Set(ids.map(id => `${id}`))].filter(Boolean);
  if (!uniqueIds.length) return {};
  const chunks: string[][] = [];
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map(chunk =>
      feathers.getService('districts').find({
        query: { _id: { $in: chunk } },
        paginate: false
      })
    )
  );
  const byId: Record<string, DistrictDisplay> = {};
  for (const res of results) {
    const list = Array.isArray(res) ? res : (res.data ?? []);
    list.forEach((d: District) => {
      byId[`${d._id}`] = { _id: d._id, longName: d.longName };
    });
  }
  return byId;
}

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const SupportedDistrictRow = styled.div`
	display: grid;
	grid-template-columns: 1fr auto;
	align-items: center;
	grid-gap: 12px;
	border-radius: 4px;
	padding: 8px;
	&:hover {
		background-color: #dddddd;
		transform: scale(1.01);
	}
`;

export { EditCoveragePlan };
